/**
 * Servicio PDP gallery carousel + lightbox (with touch swipe).
 */

const initServicioGallery = () => {
  const root = document.querySelector('[data-spd-gallery]');
  if (!root) return;

  const track = root.querySelector('[data-spd-gallery-track]');
  const viewport = root.querySelector('[data-spd-gallery-viewport]');
  const slides = [...root.querySelectorAll('.spd-gallery-slide')];
  const prevBtn = root.querySelector('[data-spd-gallery-prev]');
  const nextBtn = root.querySelector('[data-spd-gallery-next]');
  const openBtns = [...root.querySelectorAll('[data-spd-gallery-open]')];

  const lightbox = root.querySelector('[data-spd-lightbox]');
  const lbImg = root.querySelector('[data-spd-lightbox-img]');
  const lbCounter = root.querySelector('[data-spd-lightbox-counter]');
  const lbPrev = root.querySelector('[data-spd-lightbox-prev]');
  const lbNext = root.querySelector('[data-spd-lightbox-next]');
  const lbCloseBtns = [...root.querySelectorAll('[data-spd-lightbox-close]')];

  if (!track || !slides.length) return;

  // Gallery track uses transform; fixed children would anchor to that box.
  // Portal the lightbox to <body> so it always covers the full viewport.
  if (lightbox && lightbox.parentElement !== document.body) {
    document.body.appendChild(lightbox);
  }

  let index = 0;
  let lbIndex = 0;

  const gap = () => {
    const style = getComputedStyle(track);
    return parseFloat(style.columnGap || style.gap) || 16;
  };

  const visibleCount = () => {
    if (window.matchMedia('(max-width: 767px)').matches) return 1;
    if (window.matchMedia('(max-width: 899px)').matches) return 2;
    return 3;
  };

  const maxIndex = () => Math.max(0, slides.length - visibleCount());

  const updateNav = () => {
    const max = maxIndex();
    if (prevBtn) {
      prevBtn.disabled = index <= 0;
      prevBtn.hidden = slides.length <= 3;
    }
    if (nextBtn) {
      nextBtn.disabled = index >= max;
      nextBtn.hidden = slides.length <= 3;
    }
  };

  const goTo = (next) => {
    index = Math.min(Math.max(0, next), maxIndex());
    const slide = slides[0];
    const step = slide.getBoundingClientRect().width + gap();
    track.style.transform = `translate3d(-${index * step}px, 0, 0)`;
    updateNav();
  };

  prevBtn?.addEventListener('click', () => goTo(index - 1));
  nextBtn?.addEventListener('click', () => goTo(index + 1));
  window.addEventListener('resize', () => goTo(index));

  const bindSwipe = (el, onLeft, onRight) => {
    if (!el) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    el.addEventListener(
      'touchstart',
      (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      },
      { passive: true },
    );

    el.addEventListener(
      'touchend',
      (e) => {
        if (!tracking || !e.changedTouches || !e.changedTouches.length) return;
        tracking = false;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) onLeft();
        else onRight();
      },
      { passive: true },
    );
  };

  bindSwipe(
    viewport || track,
    () => goTo(index + 1),
    () => goTo(index - 1),
  );

  const sources = openBtns.map((btn) => ({
    full: btn.getAttribute('data-full') || btn.querySelector('img')?.src || '',
    alt: btn.querySelector('img')?.alt || '',
  }));

  const syncLightbox = () => {
    if (!lbImg || !sources[lbIndex]) return;
    lbImg.src = sources[lbIndex].full;
    lbImg.alt = sources[lbIndex].alt;
    if (lbCounter) {
      lbCounter.hidden = sources.length < 2;
      lbCounter.textContent = `${lbIndex + 1} / ${sources.length}`;
    }
    if (lbPrev) lbPrev.hidden = sources.length < 2;
    if (lbNext) lbNext.hidden = sources.length < 2;
  };

  const openLightbox = (i) => {
    if (!lightbox) return;
    lbIndex = i;
    syncLightbox();
    lightbox.hidden = false;
    document.documentElement.classList.add('spd-lightbox-open');
    lightbox.querySelector('.spd-lightbox-close')?.focus();
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    lightbox.hidden = true;
    document.documentElement.classList.remove('spd-lightbox-open');
  };

  openBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.getAttribute('data-index') || '0', 10);
      openLightbox(Number.isNaN(i) ? 0 : i);
    });
  });

  lbCloseBtns.forEach((btn) => btn.addEventListener('click', closeLightbox));
  lbPrev?.addEventListener('click', () => {
    lbIndex = (lbIndex - 1 + sources.length) % sources.length;
    syncLightbox();
  });
  lbNext?.addEventListener('click', () => {
    lbIndex = (lbIndex + 1) % sources.length;
    syncLightbox();
  });

  bindSwipe(
    lightbox?.querySelector('.spd-lightbox-dialog') || lightbox,
    () => {
      if (!lightbox || lightbox.hidden || sources.length < 2) return;
      lbIndex = (lbIndex + 1) % sources.length;
      syncLightbox();
    },
    () => {
      if (!lightbox || lightbox.hidden || sources.length < 2) return;
      lbIndex = (lbIndex - 1 + sources.length) % sources.length;
      syncLightbox();
    },
  );

  document.addEventListener('keydown', (e) => {
    if (!lightbox || lightbox.hidden) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') {
      lbIndex = (lbIndex - 1 + sources.length) % sources.length;
      syncLightbox();
    }
    if (e.key === 'ArrowRight') {
      lbIndex = (lbIndex + 1) % sources.length;
      syncLightbox();
    }
  });

  updateNav();
  goTo(0);
};

export default initServicioGallery;
